import { ButtonIcon, Card, CardButtonActionMode, HeaderGradient, MiniCard, Row } from '@app/components';
import React from 'react';
import SvgInfo from '@app/components/icons/SvgInfo';
import SvgStats from '@app/components/icons/SvgStats';
import SvgList from '@app/components/icons/SvgList';
import { SlantTitle } from '@app/components/typography';
import { theme } from '@app/theme';
import { useDynamicSizes } from '@app/hooks';
import LinearGradient from 'react-native-linear-gradient';
import { StyleSheet } from 'react-native';
import { IPack } from '@app/common';
import { UseFormReturn, useWatch } from 'react-hook-form';
import SvgImage from '@app/components/icons/SvgImage';

type PackCardProps = {
  pack?: Partial<IPack>;
  formMethods: UseFormReturn<any, any, any>;
  onOpenImagePicker: () => void;
  toggle: (mode: CardButtonActionMode) => void;
};

const formatCardData = (partialPack: Partial<IPack>) => {
  return {
    name: partialPack?.name || 'Nouveau menu',
    tags: partialPack?.tags?.map(tag => tag.name).join(', ') || '-',
    totalOfPackToSteps: partialPack?.packToSteps?.length || '-',
    catalogs: partialPack?.catalogToPacks?.map(catalogToPack => (catalogToPack?.catalog?.name.replace('Catalogue', '')) || '').join(', ') || '-'
  }
}

export const PackCard = ({ pack, toggle, formMethods, onOpenImagePicker }: PackCardProps) => {
  const { subContainerHeight } = useDynamicSizes();

  const cardData = useWatch({
    control: formMethods.control,
  });

  const {
    name,
    tags,
    catalogs,
    totalOfPackToSteps
  } = formatCardData(cardData);

  const previewUrl = cardData.image ? URL.createObjectURL(cardData.image) : null;

  return (
    <Card>

      <HeaderGradient>
        <LinearGradient
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          locations={[0, 1]}
          colors={['rgba(253, 255, 252, 0)', 'rgba(253, 255, 252, 1)']}
          style={styles.headerGradient}
        />
      </HeaderGradient>

      <Card.TopHeader>
        <Card.TopHeaderLabel>Fiche menu</Card.TopHeaderLabel>
      </Card.TopHeader>

      <Card.Container height={subContainerHeight}>
        <Card.Background source={require('@app/assets/img/cardbackground.png')} />

        <Card.Content>
          <Card.Header>
            <Row justify="space-between" align="center">
              <Card.HeaderTitles>
                <Card.Title numberOfLines={2}>{name}</Card.Title>
                {pack?._id && <Card.Subtitle>Ref: {pack._id}</Card.Subtitle>}
              </Card.HeaderTitles>

              <Card.ButtonAction onPress={toggle} permissionScope="packs" />
            </Row>

            <Card.HeaderValues>
              <Card.HeaderValuesItem>
                <Card.Key numberOfLines={1}>Catégories</Card.Key>
                <Card.Value numberOfLines={3}>{tags}</Card.Value>
              </Card.HeaderValuesItem>
              <Card.HeaderValuesItem>
                <Card.Key numberOfLines={1}>Etapes</Card.Key>
                <Card.Value>{totalOfPackToSteps}</Card.Value>
              </Card.HeaderValuesItem>
              <Card.HeaderValuesItem>
                <Card.Key numberOfLines={1}>Catalogues</Card.Key>
                <Card.Value numberOfLines={3}>{catalogs}</Card.Value>
              </Card.HeaderValuesItem>
            </Card.HeaderValues>

          </Card.Header>

          <Card.VisualContainer>
            <Card.Visual source={{ uri: previewUrl ? previewUrl : pack?.images?.xxlarge?.webp?.url }} />

            <Card.VisualEdit>
              <ButtonIcon Icon={SvgImage} onPress={() => onOpenImagePicker()} />
            </Card.VisualEdit>
          </Card.VisualContainer>
        </Card.Content>

        <MiniCard>
          <MiniCard.Tabs>
            <MiniCard.TabsHolder>
              <MiniCard.Tab Icon={SvgInfo} isActive />
            </MiniCard.TabsHolder>
          </MiniCard.Tabs>
          <MiniCard.Container>
            <MiniCard.Content>
              <SlantTitle label="Aide" color={theme.colors.gold} />

              <Card.HelpScrollContainer>
                <Card.LineBreak height={16} />

                <Card.HelpTitle>Fonctionnement des menus</Card.HelpTitle>

                <Card.LineBreak height={8} />

                <Card.HelpText>
                  Un menu est composé d’étapes, elles-mêmes composées de produits.
                  Avant de vous lancer dans la création d’un menu, créez d’abord les produits, puis les étapes.
                </Card.HelpText>

                <Card.LineBreak height={8} />

                <Card.HelpText>
                  Si un produit est inclus par défaut dans le menu, il n’est pas nécessaire de créer une étape: vous pouvez directement l’ajouter dans le champ "Produits offerts".
                </Card.HelpText>

                <Card.LineBreak height={8} />

                <Card.HelpText>
                  Vous pouvez également personnaliser vos menus en les rendant disponibles à certaines heures, certains jours, ou sur certains modes de commande uniquement.
                </Card.HelpText>

                <Card.LineBreak height={16} />

                <Card.HelpText>
                  Pour suivre le tutoriel complet, rendez-vous{' '}
                  <Card.HelpLink href="https://tickeat.featurebase.app/en/help/articles/0503741-creer-un-menu-sur-tickeat">
                    ici
                  </Card.HelpLink>
                </Card.HelpText>

              </Card.HelpScrollContainer>
            </MiniCard.Content>
          </MiniCard.Container>
        </MiniCard>

      </Card.Container>
    </Card>
  );
};

const styles = StyleSheet.create({
  headerGradient: {
    flex: 1,
    height: theme.sizes.listHeaderHeight,
  },
});
